import { Button, ColorInput, Group, Modal, Select, Stack, Switch, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClients, useDuplicateProject, useProjects, useSaveProject, type Project } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { useApiError } from '@/hooks/useApiError';
import { PROJECT_COLORS, randomProjectColor } from './colors';

interface Props {
  opened: boolean;
  onClose: () => void;
  project?: Project;
  initialName?: string;
  onSaved?: (project: Project) => void;
}

/** Projekt anlegen/bearbeiten (PRJ-01), optional aus Vorlage (PRJ-07). */
export function ProjectModal({ opened, onClose, project, initialName, onSaved }: Props) {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { workspaceId } = ctx;
  const { data: clients = [] } = useClients(workspaceId);
  const { data: templates = [] } = useProjects(workspaceId, { template: true, archived: false });
  const save = useSaveProject(workspaceId);
  const duplicate = useDuplicateProject(workspaceId);
  const onError = useApiError();
  const [initialColor] = useState(randomProjectColor);
  const form = useForm({
    initialValues: {
      name: '',
      clientId: '' as string | null,
      color: initialColor,
      isPublic: true,
      billable: true,
      templateId: null as string | null,
    },
    validate: { name: (v) => (v.trim() ? null : t('common.required')) },
  });

  useEffect(() => {
    if (opened) {
      form.setValues({
        name: project?.name ?? initialName ?? '',
        clientId: project?.clientId ?? null,
        color: project?.color ?? form.values.color,
        isPublic: project?.isPublic ?? true,
        billable: project?.billable ?? true,
        templateId: null,
      });
    }
  }, [opened, project?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = form.onSubmit(async (values) => {
    try {
      let saved: Project;
      if (!project && values.templateId && ctx.hasFeature('PROJECT_TEMPLATES')) {
        const copy = await duplicate.mutateAsync({ id: values.templateId, name: values.name, template: false });
        saved = await save.mutateAsync({
          id: copy.id,
          clientId: values.clientId ?? undefined,
          clearClient: !values.clientId,
          color: values.color,
          isPublic: values.isPublic,
          billable: values.billable,
        });
      } else {
        saved = await save.mutateAsync({
          id: project?.id,
          name: values.name,
          clientId: values.clientId ?? undefined,
          clearClient: !values.clientId,
          color: values.color,
          isPublic: values.isPublic,
          billable: values.billable,
        });
      }
      onSaved?.(saved);
      onClose();
    } catch (e) {
      onError(e);
    }
  });

  return (
    <Modal opened={opened} onClose={onClose} title={project ? t('projects.edit') : t('projects.create')}>
      <form onSubmit={submit}>
        <Stack>
          <TextInput label={t('projects.name')} data-autofocus {...form.getInputProps('name')} />
          <Select
            label={t('projects.client')}
            data={clients.filter((c) => !c.archived || c.id === form.values.clientId).map((c) => ({ value: c.id, label: c.name }))}
            clearable
            searchable
            placeholder={t('projects.noClient')}
            {...form.getInputProps('clientId')}
          />
          {!project && ctx.hasFeature('PROJECT_TEMPLATES') && templates.length > 0 && (
            <Select
              label={t('projects.fromTemplate')}
              data={templates.map((p) => ({ value: p.id, label: p.name }))}
              clearable
              {...form.getInputProps('templateId')}
            />
          )}
          <ColorInput label={t('projects.color')} swatches={PROJECT_COLORS} swatchesPerRow={6} withPicker={false} {...form.getInputProps('color')} />
          <Group>
            <Switch label={t('projects.public')} {...form.getInputProps('isPublic', { type: 'checkbox' })} />
            <Switch label={t('projects.billable')} {...form.getInputProps('billable', { type: 'checkbox' })} />
          </Group>
          <Button type="submit" loading={save.isPending || duplicate.isPending}>
            {project ? t('common.save') : t('common.create')}
          </Button>
        </Stack>
      </form>
    </Modal>
  );
}
